import { BottomSheet, CbhIcon, Text, Title, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogContent, MenuItem, Select, Stack } from "@mui/material";
import { formatCentsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { type JobType } from "@src/appV2/redesign/PlacementCandidate/types";
import pluralize from "pluralize";
import { useFormContext } from "react-hook-form";

import { DialogFooter } from "../../../components/DialogFooter";
import { type TransparencyPayRate } from "../../types/payRate";
import { getLowestMinYearsOfExperience } from "../../utils/pay-transparency";
import { type PayAndScheduleFormData } from "./usePayAndScheduleForm";

interface YearsOfExperienceInputChipProps {
  value: number | undefined;
  onMenuOpen: () => void;
  onChange: (value: number) => void;
  max: number;
  baseLabel: string;
  transparencyPayRates: TransparencyPayRate[];
  selectedJobType: JobType;
}

export function YearsOfExperienceInputChip(props: YearsOfExperienceInputChipProps) {
  const { baseLabel, value, onMenuOpen, onChange, max, transparencyPayRates, selectedJobType } =
    props;

  const lowestMinYearsOfExperience = getLowestMinYearsOfExperience(
    transparencyPayRates,
    selectedJobType
  );

  const inputModalState = useModalState();

  const yearsOfExperienceOptions = Array.from({ length: max + 1 }, (_, index) => index);

  const { watch } = useFormContext<PayAndScheduleFormData>();

  const jobType = watch("jobType");

  const yearsOfExperienceBaseRates = isDefined(jobType)
    ? yearsOfExperienceOptions.map((yearsOfExperience) => {
        return transparencyPayRates.reduce((accumulator: number | undefined, payRate) => {
          if (
            payRate.minYearsOfExperience <= yearsOfExperience &&
            payRate.jobTypes.includes(jobType) &&
            payRate.baseRate > (accumulator ?? 0)
          ) {
            return payRate.baseRate;
          }

          return accumulator;
        }, undefined);
      })
    : undefined;

  const firstYearsOfExperienceBaseRate = yearsOfExperienceBaseRates?.find((baseRate) =>
    isDefined(baseRate)
  );

  const yearsOfExperienceBaseRateDiffs =
    isDefined(yearsOfExperienceBaseRates) && isDefined(firstYearsOfExperienceBaseRate)
      ? yearsOfExperienceBaseRates.map((baseRate) =>
          isDefined(baseRate) ? baseRate - firstYearsOfExperienceBaseRate : undefined
        )
      : undefined;

  const getYearsOfExperienceDiffLabel = (years: number, lowestMinYearsOfExperience: number) => {
    if (!isDefined(yearsOfExperienceBaseRateDiffs)) {
      return "";
    }

    const diff = yearsOfExperienceBaseRateDiffs.at(years);

    if (!isDefined(diff)) {
      return `Min. ${lowestMinYearsOfExperience} ${pluralize(
        "year",
        lowestMinYearsOfExperience
      )} of experience`;
    }

    if (!isDefined(diff)) {
      return "No pay data";
    }

    return `+ ${formatCentsAsUsd(diff)}`;
  };

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={(theme) => ({
        borderRadius: 12,
        border: `1px solid ${theme.palette.border?.primary}`,
        background: theme.palette.background.tertiary,
        minWidth: 96,
        minHeight: 40,
        py: 3,
        px: 4,
        alignItems: "center",
        justifyContent: "center",
      })}
      onClick={() => {
        if (inputModalState.modalIsClosed) {
          inputModalState.openModal();
        }
      }}
    >
      <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.primary })}>
        {isDefined(value) ? `${value} ${pluralize("year", value)}` : baseLabel}
      </Text>
      <CbhIcon type="pencil" size="small" />

      <BottomSheet
        modalState={inputModalState}
        footer={
          <DialogFooter
            orientation="vertical"
            backgroundColor={(theme) => theme.palette.background.tertiary}
            onClose={(event) => {
              inputModalState.closeModal();
              event.preventDefault();
            }}
          />
        }
      >
        <DialogContent>
          <Stack spacing={6} mt={4}>
            <Title variant="h4" component="h1">
              Years of experience
            </Title>
            <Text variant="body1">
              We&apos;ll show you what this workplace pays based on your years of experience
            </Text>
            {isDefined(value) && value < lowestMinYearsOfExperience && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CbhIcon type="remove" size="small" color={(theme) => theme.palette.error.main} />
                <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.tertiary })}>
                  Sorry, we don&apos;t have any pay data for your level of experience
                </Text>
              </Stack>
            )}
            <Select
              fullWidth
              variant="outlined"
              value={value?.toString() ?? "-1"}
              renderValue={(value) => {
                if (value === "-1") {
                  return "Please select a value";
                }

                if (value === max.toString()) {
                  return `${value} +`;
                }

                return value;
              }}
              onClick={onMenuOpen}
              onChange={(event) => {
                onChange(Number.parseInt(event.target.value, 10));
                inputModalState.closeModal();
              }}
            >
              {!isDefined(value) && <MenuItem value="-1">Please select a value</MenuItem>}
              {yearsOfExperienceOptions.map((years) => {
                const diffLabel = getYearsOfExperienceDiffLabel(years, lowestMinYearsOfExperience);

                const hasNoPayData = diffLabel.toLowerCase().startsWith("min.");

                return (
                  <MenuItem key={years} value={years.toString()} disabled={hasNoPayData}>
                    {years}
                    {years === max ? " +" : ""}
                    {isDefined(diffLabel) && (
                      <Text
                        variant="body2"
                        ml={2}
                        sx={(theme) => ({ color: theme.palette.text.tertiary })}
                      >
                        {diffLabel}
                      </Text>
                    )}
                  </MenuItem>
                );
              })}
            </Select>
          </Stack>
        </DialogContent>
      </BottomSheet>
    </Stack>
  );
}
